import type { CofQuizSubMode } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import styles from './cof-quiz-mode-picker.module.css'

export interface CofQuizModePickerProps {
  onStart: (subMode: CofQuizSubMode) => void
}

/**
 * CoF Wheel v1.5 quiz sub-mode picker — three cards (Mixed / Relationships /
 * Signatures) mounted below the Explore card on the welcome surface. Card
 * bodies pull from the lexicon via `useTheoryCopy`. Explicit `aria-label`s keep
 * the tests decoupled from Bowie's eventual copy edits.
 *
 * Spec: docs/superpowers/specs/2026-05-28-cof-wheel-v1.5-design.md §6.1
 */
export const CofQuizModePicker: FC<CofQuizModePickerProps> = props => {
  const { onStart } = props
  const mixedCopy = useTheoryCopy('cof-quiz')
  const relationshipsCopy = useTheoryCopy('cof-quiz-relationships')
  const signaturesCopy = useTheoryCopy('cof-quiz-signatures')

  return (
    <div className={styles.picker} role="group" aria-label="Quiz sub-mode picker">
      <Button
        type="button"
        variant="outlined"
        aria-label="Mixed quiz"
        onClick={() => onStart('mixed')}
        className={styles.card}
      >
        <span className={styles.cardTitle}>{mixedCopy?.displayName ?? 'Mixed'}</span>
        {mixedCopy?.body ? <span className={styles.cardBody}>{mixedCopy.body}</span> : null}
      </Button>
      <Button
        type="button"
        variant="outlined"
        aria-label="Relationships quiz"
        onClick={() => onStart('relationships')}
        className={styles.card}
      >
        <span className={styles.cardTitle}>{relationshipsCopy?.displayName ?? 'Relationships'}</span>
        {relationshipsCopy?.body ? <span className={styles.cardBody}>{relationshipsCopy.body}</span> : null}
      </Button>
      <Button
        type="button"
        variant="outlined"
        aria-label="Signatures quiz"
        onClick={() => onStart('signatures')}
        className={styles.card}
      >
        <span className={styles.cardTitle}>{signaturesCopy?.displayName ?? 'Signatures'}</span>
        {signaturesCopy?.body ? <span className={styles.cardBody}>{signaturesCopy.body}</span> : null}
      </Button>
    </div>
  )
}
